import "./App.css";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "./lib/supabase";
import { audit } from "./lib/audit";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "./context/AppContext";
import { RoleRoute } from "./components/RoleRoute";
import { DesktopLayout } from "./components/DesktopLayout";
import {
  ClipboardList,
  FileText,
  Search,
  CalendarDays,
  UserCircle,
  ShieldCheck,
  Download,
  Eye,
  Activity,
  AlertTriangle,
  Footprints,
  Siren,
  Repeat,
  Lock,
  X,
  Printer,
} from "lucide-react";

import {
  registrosSistema,
  modelosRelatorio,
  vigilantes,
  ocorrencias,
  rondas,
  emergencias,
  passagensTurno,
  postos,
  formatDataHora,
} from "./data/mock";

// Login
import LoginPage from "./pages/Login";

// QR Ronda scan (sem autenticação obrigatória)
import RondaQR from "./pages/vigilante/RondaQR";

// Admin
import QRCodesPage from "./pages/admin/QRCodes";

// Vigilante mobile
import PlantaoHome from "./pages/vigilante/PlantaoHome";
import RondasMobile from "./pages/vigilante/RondasMobile";
import OcorrenciasMobile from "./pages/vigilante/OcorrenciasMobile";
import PassagemTurnoMobile from "./pages/vigilante/PassagemTurnoMobile";
import ChecklistPage from "./pages/vigilante/Checklist";
import HistoricoPage from "./pages/vigilante/Historico";
import PassagemPonto from "./pages/vigilante/PassagemPonto";

// Supervisor
import FilaAprovacao from "./pages/supervisor/FilaAprovacao";
import EfetivoAtual from "./pages/supervisor/EfetivoAtual";
import PassagemPlantao from "./pages/supervisor/PassagemPlantao";
import TreinamentosSupervision from "./pages/supervisor/TreinamentosSupervision";

// Vigilante — treinamento
import TreinamentoPage from "./pages/vigilante/Treinamento";
import PerfilPage from "./pages/vigilante/Perfil";
import AvisosPage from "./pages/vigilante/Avisos";

// Síndico / Administração desktop
import Dashboard from "./pages/sindico/Dashboard";
import RondasDesktop from "./pages/sindico/Rondas";
import OcorrenciasDesktop from "./pages/sindico/Ocorrencias";
import PassagemTurnoDesktop from "./pages/sindico/PassagemTurno";
import VigilantesDesktop from "./pages/sindico/Vigilantes";
import EscalaDesktop from "./pages/sindico/Escala";

const desktopRoles = ["sindico", "admin", "supervisor", "liberty", "esquematiza"];

const ROTA_LABEL = {
  "/vigilante":            "Plantão",
  "/rondas":               "Rondas",
  "/ocorrencias":          "Ocorrências",
  "/emergencias":          "Emergências",
  "/historico":            "Histórico",
  "/checklist":            "Checklist",
  "/dashboard":            "Dashboard",
  "/registros":            "Registros",
  "/relatorios":           "Relatórios",
  "/sindico":              "Vigilantes",
  "/passagem-turno":       "Passagem de Turno",
  "/supervisor/ocorrencias":   "Fila de Aprovação",
  "/supervisor/efetivo":       "Efetivo Atual",
  "/supervisor/passagem":      "Passagem de Plantão",
  "/treinamento":              "Treinamento",
  "/supervisor/treinamentos":  "Treinamentos",
  "/avisos":                   "Avisos",
  "/escala":                   "Escala",
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const RouteAudit = () => {
  const location = useLocation();
  const { user } = useApp();
  useEffect(() => {
    if (!user) return;
    const nome = ROTA_LABEL[location.pathname] || location.pathname;
    audit({
      usuario_nome: user.nome || user.usuarioNome,
      usuario_role: user.role,
      acao: "navegacao",
      modulo: nome,
      descricao: `Usuário acessou ${nome}`,
    });
  }, [location.pathname]); // eslint-disable-line
  return null;
};

const RootRedirect = () => {
  const { user } = useApp();

  if (!user) return <Navigate to="/login" replace />;

  return (
    <Navigate
      to={user.role === "vigilante" ? "/vigilante" : "/dashboard"}
      replace
    />
  );
};

const ResponsiveRoute = ({ mobile, desktop }) => {
  const { user } = useApp();

  if (!user) return <Navigate to="/login" replace />;

  if (user.role === "vigilante") return mobile;

  if (desktopRoles.includes(user.role)) return desktop;

  return <Navigate to="/login" replace />;
};

const getPostoNome = (postoId) => {
  return postos.find((p) => p.id === postoId)?.nome || "Posto não informado";
};

const getVigilanteNome = (vigilanteId) => {
  return (
    vigilantes.find((v) => v.id === vigilanteId)?.nome ||
    "Vigilante não informado"
  );
};

const getRegistrosApresentacao = () => {
  const acessosLocal = JSON.parse(localStorage.getItem("vigia_acessos") || "[]");

  const registrosOperacionais = [
    ...registrosSistema,
    ...acessosLocal,
    ...rondas.map((ronda) => ({
      id: `log-ronda-${ronda.id}`,
      usuarioId: ronda.vigilanteId,
      usuarioNome: getVigilanteNome(ronda.vigilanteId),
      tipoUsuario: "vigilante",
      acao:
        ronda.status === "concluida" ? "ronda_concluida" : "ronda_iniciada",
      modulo: "Rondas",
      descricao:
        ronda.status === "concluida" ? "Ronda concluída." : "Ronda iniciada.",
      detalhes: `${getPostoNome(ronda.postoId)} · ${
        ronda.pontosVisitados?.length || 0
      } ponto(s) visitado(s).`,
      criadoEm: ronda.fim || ronda.inicio,
    })),
    ...ocorrencias.map((ocorrencia) => ({
      id: `log-oc-${ocorrencia.id}`,
      usuarioId: ocorrencia.vigilanteId,
      usuarioNome: getVigilanteNome(ocorrencia.vigilanteId),
      tipoUsuario: "vigilante",
      acao: "ocorrencia_criada",
      modulo: "Ocorrências",
      descricao: "Ocorrência registrada.",
      detalhes: `${ocorrencia.titulo} · Prioridade ${ocorrencia.prioridade}.`,
      criadoEm: ocorrencia.criadoEm,
    })),
    ...emergencias.map((emergencia) => ({
      id: `log-em-${emergencia.id}`,
      usuarioId: emergencia.vigilanteId,
      usuarioNome: getVigilanteNome(emergencia.vigilanteId),
      tipoUsuario: "vigilante",
      acao: "emergencia_acionada",
      modulo: "Emergências",
      descricao: "Emergência acionada.",
      detalhes: emergencia.observacao || "Acionamento emergencial registrado.",
      criadoEm: emergencia.criadoEm,
    })),
    ...passagensTurno.map((passagem) => ({
      id: `log-pt-${passagem.id}`,
      usuarioId: passagem.deVigilanteId,
      usuarioNome: getVigilanteNome(passagem.deVigilanteId),
      tipoUsuario: "vigilante",
      acao: "passagem_turno",
      modulo: "Passagem de Turno",
      descricao: "Passagem de turno registrada.",
      detalhes: passagem.resumo,
      criadoEm: passagem.criadoEm,
    })),
  ];

  return registrosOperacionais
    .filter(Boolean)
    .sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm));
};

const getAcaoStyle = (acao) => {
  if (acao?.includes("emergencia")) {
    return {
      icon: Siren,
      badge: "bg-red-500/10 text-red-200 border-red-500/20",
      dot: "bg-red-400",
    };
  }

  if (acao?.includes("ocorrencia")) {
    return {
      icon: AlertTriangle,
      badge: "bg-amber-500/10 text-amber-200 border-amber-500/20",
      dot: "bg-amber-400",
    };
  }

  if (acao?.includes("ronda")) {
    return {
      icon: Footprints,
      badge: "bg-emerald-500/10 text-emerald-200 border-emerald-500/20",
      dot: "bg-emerald-400",
    };
  }

  if (acao?.includes("senha") || acao?.includes("login")) {
    return {
      icon: Lock,
      badge: "bg-blue-500/10 text-blue-200 border-blue-500/20",
      dot: "bg-blue-400",
    };
  }

  if (acao?.includes("passagem")) {
    return {
      icon: Repeat,
      badge: "bg-purple-500/10 text-purple-200 border-purple-500/20",
      dot: "bg-purple-400",
    };
  }

  return {
    icon: Activity,
    badge: "bg-white/5 text-slate-300 border-white/10",
    dot: "bg-slate-400",
  };
};

const gerarConteudoRelatorio = (tipo, nivel) => {
  const agora = new Date().toLocaleString("pt-BR");
  const linhas = [];

  linhas.push("GRUPO ESQUEMATIZA");
  linhas.push("VIGIA COMANDO · PLATAFORMA GERENCIAL");
  linhas.push("CONDOMÍNIO: LIBERTY PLACE");
  linhas.push("ADMINISTRAÇÃO: ROBSON FREITAS");
  linhas.push(`GERADO EM: ${agora}`);
  linhas.push("");
  linhas.push("============================================");
  linhas.push(
    nivel === "detalhado"
      ? "RELATÓRIO DETALHADO"
      : "RELATÓRIO BÁSICO",
  );
  linhas.push("============================================");
  linhas.push("");

  if (tipo === "geral") {
    linhas.push("1. RESUMO GERAL DO PLANTÃO");
    linhas.push(`- Vigilantes cadastrados: ${vigilantes.length}`);
    linhas.push(`- Vigilantes em serviço: ${vigilantes.filter((v) => v.emServico).length}`);
    linhas.push(`- Rondas registradas: ${rondas.length}`);
    linhas.push(`- Ocorrências abertas: ${ocorrencias.length}`);
    linhas.push(`- Emergências registradas: ${emergencias.length}`);
    linhas.push(`- Passagens de turno: ${passagensTurno.length}`);
    linhas.push("");

    linhas.push("2. SITUAÇÃO OPERACIONAL");
    linhas.push(
      "A operação encontra-se em acompanhamento ativo, com registros de ronda, controle de ocorrências e histórico de plantão disponíveis para consulta.",
    );
  }

  if (tipo === "rondas") {
    linhas.push("1. RELATÓRIO DE RONDAS");
    rondas.forEach((ronda, index) => {
      linhas.push("");
      linhas.push(`${index + 1}. Ronda ${ronda.status}`);
      linhas.push(`- Vigilante: ${getVigilanteNome(ronda.vigilanteId)}`);
      linhas.push(`- Posto: ${getPostoNome(ronda.postoId)}`);
      linhas.push(`- Início: ${formatDataHora(ronda.inicio)}`);
      linhas.push(`- Fim: ${formatDataHora(ronda.fim)}`);
      linhas.push(`- Pontos visitados: ${ronda.pontosVisitados?.length || 0}`);
    });
  }

  if (tipo === "ocorrencias") {
    linhas.push("1. RELATÓRIO DE OCORRÊNCIAS");
    ocorrencias.forEach((ocorrencia, index) => {
      linhas.push("");
      linhas.push(`${index + 1}. ${ocorrencia.titulo}`);
      linhas.push(`- Vigilante: ${getVigilanteNome(ocorrencia.vigilanteId)}`);
      linhas.push(`- Categoria: ${ocorrencia.categoria}`);
      linhas.push(`- Prioridade: ${ocorrencia.prioridade}`);
      linhas.push(`- Status: ${ocorrencia.status}`);
      linhas.push(`- Data: ${formatDataHora(ocorrencia.criadoEm)}`);
      linhas.push(`- Descrição: ${ocorrencia.descricao}`);
    });
  }

  if (tipo === "emergencias") {
    linhas.push("1. RELATÓRIO DE EMERGÊNCIAS");
    emergencias.forEach((emergencia, index) => {
      linhas.push("");
      linhas.push(`${index + 1}. Emergência registrada`);
      linhas.push(`- Vigilante: ${getVigilanteNome(emergencia.vigilanteId)}`);
      linhas.push(`- Posto: ${getPostoNome(emergencia.postoId)}`);
      linhas.push(`- Data: ${formatDataHora(emergencia.criadoEm)}`);
      linhas.push(`- Status: ${emergencia.status}`);
      linhas.push(`- Observação: ${emergencia.observacao}`);
    });
  }

  if (tipo === "vigilante") {
    linhas.push("1. RELATÓRIO POR VIGILANTE");
    vigilantes.forEach((vigilante, index) => {
      const rondasVigilante = rondas.filter(
        (r) => r.vigilanteId === vigilante.id,
      );
      const ocorrenciasVigilante = ocorrencias.filter(
        (o) => o.vigilanteId === vigilante.id,
      );

      linhas.push("");
      linhas.push(`${index + 1}. ${vigilante.nome}`);
      linhas.push(`- E-mail: ${vigilante.email}`);
      linhas.push(`- Matrícula: ${vigilante.matricula}`);
      linhas.push(`- Posto: ${getPostoNome(vigilante.postoId)}`);
      linhas.push(`- Status: ${vigilante.emServico ? "Em serviço" : "Fora de serviço"}`);
      linhas.push(`- Rondas registradas: ${rondasVigilante.length}`);
      linhas.push(`- Ocorrências registradas: ${ocorrenciasVigilante.length}`);
    });
  }

  if (tipo === "executivo") {
    linhas.push("1. RESUMO EXECUTIVO PARA O SÍNDICO");
    linhas.push("");
    linhas.push("Indicadores de ronda:");
    linhas.push(`- ${vigilantes.length} vigilantes cadastrados na operação`);
    linhas.push(`- ${rondas.length} rondas registradas no período`);
    linhas.push(`- Pontos de ronda confirmados via QR Code`);
    linhas.push("");
    linhas.push("Análise:");
    linhas.push(
      "O sistema registra em tempo real os pontos de ronda confirmados via QR Code, permitindo ao síndico acompanhar a cobertura e a produtividade de cada vigilante com transparência e rastreabilidade.",
    );
  }

  if (nivel === "detalhado") {
    linhas.push("");
    linhas.push("============================================");
    linhas.push("DETALHAMENTO COMPLEMENTAR");
    linhas.push("============================================");
    linhas.push("");
    linhas.push("REGISTROS DO SISTEMA:");
    getRegistrosApresentacao()
      .slice(0, 12)
      .forEach((registro, index) => {
        linhas.push("");
        linhas.push(`${index + 1}. ${registro.modulo} · ${registro.descricao}`);
        linhas.push(`- Responsável: ${registro.usuarioNome}`);
        linhas.push(`- Data/Hora: ${formatDataHora(registro.criadoEm)}`);
        linhas.push(`- Detalhes: ${registro.detalhes}`);
      });

    linhas.push("");
    linhas.push("OBSERVAÇÃO:");
    linhas.push(
      "Relatório demonstrativo gerado com dados mockados para apresentação do MVP. Na versão com Supabase, os dados serão registrados em banco real com auditoria e rastreabilidade.",
    );
  }

  linhas.push("");
  linhas.push("============================================");
  linhas.push("FIM DO RELATÓRIO");
  linhas.push("============================================");

  return linhas.join("\n");
};

const RegistrosPage = () => {
  const [filtro, setFiltro] = useState("todos");
  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => {
    supabase
      .from("system_audit_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(300)
      .then(({ data, error }) => {
        if (error) console.error("[Registros] audit_log:", error.message);
        setAuditLog(data || []);
      });
  }, []);

  const registrosSupabase = auditLog.map((r) => ({
    id: r.id,
    usuarioNome: r.usuario_nome || "sistema",
    tipoUsuario: r.usuario_role,
    acao: r.acao,
    modulo: r.modulo || "Sistema",
    descricao: r.descricao || r.acao || "—",
    detalhes: r.tabela || null,
    criadoEm: r.created_at,
  }));

  const base = registrosSupabase.length > 0 ? registrosSupabase : getRegistrosApresentacao();

  const registros =
    filtro === "todos"       ? base
    : filtro === "acesso"    ? base.filter((r) => ["login", "logout", "senha_alterada"].includes(r.acao))
    : filtro === "navegacao" ? base.filter((r) => r.acao === "navegacao")
    : filtro === "rondas"    ? base.filter((r) => r.acao?.includes("ronda"))
    : filtro === "ocorrencias"? base.filter((r) => r.acao?.includes("ocorrencia"))
    : base;

  const totalHoje = base.length;
  const totalAcessos = base.filter((r) => r.acao === "login" || r.acao === "logout").length;
  const totalOperacional = base.filter((r) => !["login", "logout", "senha_alterada", "navegacao"].includes(r.acao)).length;
  const totalCriticos = base.filter(
    (r) => r.acao?.includes("emergencia") || r.acao?.includes("ocorrencia"),
  ).length;

  return (
    <DesktopLayout
      title="Registros"
      subtitle="Histórico detalhado de acessos, ações e movimentações operacionais"
    >
      {/* KPI "Críticos" oculto temporariamente (escopo ronda-only) — reativar: grid-cols-4 + KpiCard Críticos */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <KpiCard titulo="Registros hoje" valor={totalHoje} detalhe="movimentações" icon={ClipboardList} />
        <KpiCard titulo="Acessos" valor={totalAcessos} detalhe="logins e senhas" icon={Lock} />
        <KpiCard titulo="Operacionais" valor={totalOperacional} detalhe="rondas e plantão" icon={Activity} />
      </div>

      <div className="rounded-2xl bg-[#0a1128] border border-white/10 overflow-hidden">
        <div className="p-5 border-b border-white/10 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <p className="tiny-label text-slate-500">Auditoria do sistema</p>
            <h2 className="text-xl font-bold mt-1">Histórico de Registros</h2>
            <p className="text-sm text-slate-400 mt-1">
              Visualização demonstrativa das ações registradas no sistema.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Filtro "Ocorrências" oculto temporariamente (escopo ronda-only) — reativar: { id: "ocorrencias", label: "Ocorrências" } */}
            {[
              { id: "todos",     label: "Todos" },
              { id: "acesso",    label: "Acesso" },
              { id: "rondas",    label: "Rondas" },
              { id: "navegacao", label: "Navegação" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFiltro(f.id)}
                className={`px-3 py-2 rounded-xl text-sm font-medium transition border ${
                  filtro === f.id
                    ? "bg-blue-500/20 border-blue-500/40 text-blue-200"
                    : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => baixarArquivo("registros-sistema.txt", gerarConteudoRelatorio("geral", "detalhado"))}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500 text-black text-sm font-semibold hover:bg-emerald-400 transition"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
          </div>
        </div>

        <div className="divide-y divide-white/5">
          {registros.map((registro) => {
            const style = getAcaoStyle(registro.acao);
            const Icon = style.icon;

            return (
              <div
                key={registro.id}
                className="p-4 hover:bg-white/[0.03] transition grid xl:grid-cols-[220px_1fr_180px] gap-4"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-xl border flex items-center justify-center ${style.badge}`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-white">
                      {registro.modulo}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDataHora(registro.criadoEm)}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                    <p className="text-sm font-semibold text-white">
                      {registro.descricao}
                    </p>
                  </div>

                  <p className="text-sm text-slate-400 leading-relaxed">
                    {registro.detalhes}
                  </p>

                  <p className="text-xs text-slate-500 mt-2">
                    Responsável:{" "}
                    <span className="text-slate-300">
                      {registro.usuarioNome}
                    </span>
                  </p>
                </div>

                <div className="flex xl:justify-end items-start">
                  <span
                    className={`inline-flex px-3 py-1 rounded-full border text-xs ${style.badge}`}
                  >
                    {registro.acao?.replaceAll("_", " ")}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DesktopLayout>
  );
};

const RelatoriosPage = () => {
  const [relatorioAberto, setRelatorioAberto] = useState(null);

  // Escopo App de Ronda: apenas Relatório de Rondas visível.
  // Ocultos temporariamente — reativar adicionando de volta ao array:
  // { id: "rel-001", nome: "Relatório geral do plantão",   tipo: "geral",       descricao: "Resumo completo com rondas, ocorrências, emergências e passagem de turno." },
  // { id: "rel-003", nome: "Relatório de ocorrências",     tipo: "ocorrencias", descricao: "Ocorrências abertas, resolvidas, prioridades e responsáveis." },
  // { id: "rel-004", nome: "Relatório de emergências",     tipo: "emergencias", descricao: "Histórico de emergências acionadas e status de atendimento." },
  // { id: "rel-005", nome: "Relatório por vigilante",      tipo: "vigilante",   descricao: "Movimentações individuais por vigilante — pontos de ronda confirmados." },
  // { id: "rel-006", nome: "Resumo executivo do síndico",  tipo: "executivo",   descricao: "Visão objetiva para o síndico: pontos confirmados, produtividade de ronda e cobertura." },
  const relatorios = [
    {
      id: "rel-002",
      nome: "Relatório de rondas",
      tipo: "rondas",
      descricao: "Lista de rondas realizadas, pontos confirmados e horários.",
    },
  ];

  const resumo = {
    vigilantes: vigilantes.length,
    rondas: rondas.length,
    // ocorrencias e emergencias ocultos no escopo ronda-only
  };

  const abrirRelatorio = (relatorio, nivel) => {
    setRelatorioAberto({
      ...relatorio,
      nivel,
      conteudo: gerarConteudoRelatorio(relatorio.tipo, nivel),
    });
  };

  return (
    <DesktopLayout
      title="Relatórios"
      subtitle="Relatórios de ronda — pontos confirmados, cobertura e produtividade"
    >
      {/* KPIs — escopo ronda-only (Ocorrências e Emergências ocultos) */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <KpiCard titulo="Vigilantes" valor={resumo.vigilantes} detalhe="cadastrados" icon={UserCircle} />
        <KpiCard titulo="Rondas" valor={resumo.rondas} detalhe="registradas" icon={Footprints} />
      </div>

      <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 mb-6">
        <p className="tiny-label text-slate-500">Resumo executivo</p>
        <h2 className="text-xl font-bold mt-1 mb-2">
          Relatório de rondas — Liberty Place
        </h2>
        <p className="text-sm text-slate-400 leading-relaxed">
          Relatórios focados no registro de rondas: pontos confirmados via QR Code,
          cobertura por vigilante e histórico de movimentações operacionais.
        </p>
      </div>

      {/* Card único — max-w-sm (grid multi-coluna oculto junto com os outros cards) */}
      <div className="max-w-sm">
        {relatorios.map((relatorio) => (
          <RelatorioCard
            key={relatorio.id}
            titulo={relatorio.nome}
            descricao={relatorio.descricao}
            tipo={relatorio.tipo}
            somenteVer
            onVer={() => abrirRelatorio(relatorio, "basico")}
            onBasico={() => abrirRelatorio(relatorio, "basico")}
            onDetalhado={() => abrirRelatorio(relatorio, "detalhado")}
          />
        ))}
      </div>

      {relatorioAberto && (
        <RelatorioModal
          relatorio={relatorioAberto}
          onClose={() => setRelatorioAberto(null)}
        />
      )}
    </DesktopLayout>
  );
};

const EMPRESA_ID_REL = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

function gerarHTMLRelatorio(dados) {
  const geradoEm   = new Date();
  const hoje       = new Date(); hoje.setHours(0, 0, 0, 0);
  const periodoLabel = hoje.toLocaleDateString("pt-BR", { weekday:"long", day:"2-digit", month:"long", year:"numeric" });

  const fmtHM  = (iso) => iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour:"2-digit", minute:"2-digit" }) : "—";
  const fmtDHM = (iso) => iso ? new Date(iso).toLocaleString("pt-BR",     { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }) : "—";

  const porHora = Array.from({ length: 24 }, (_, h) => ({
    hora:  h,
    total: dados.rondas.filter((r) => new Date(r.created_at).getHours() === h).length,
  }));
  const maxHora = Math.max(...porHora.map((h) => h.total), 1);

  const graficoHTML = porHora.map(({ hora, total }) => `
    <div class="barra-wrap">
      <div class="barra" style="height:${total > 0 ? Math.round((total / maxHora) * 84) : 2}px;${total === 0 ? "background:#f3f4f6;" : ""}"></div>
      <span class="barra-label">${String(hora).padStart(2, "0")}</span>
    </div>
  `).join("");

  const rondasHTML = dados.rondas.length === 0
    ? '<p class="vazio">Nenhum ponto registrado hoje.</p>'
    : `<table>
        <thead><tr><th>Horário</th><th>Vigilante</th><th>Ponto confirmado</th></tr></thead>
        <tbody>
          ${dados.rondas.map((r) => `
            <tr>
              <td>${fmtHM(r.created_at)}</td>
              <td>${r.vigilante_nome || "—"}</td>
              <td>${r.ponto || "—"}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>`;

  const ocorrenciasHTML = dados.ocorrencias.length === 0
    ? '<p class="vazio">Nenhuma ocorrência registrada hoje.</p>'
    : `<table>
        <thead><tr><th>Horário</th><th>Vigilante</th><th>Descrição</th><th>Status</th></tr></thead>
        <tbody>
          ${dados.ocorrencias.map((o) => {
            const s = o.status || "pendente";
            const sStyle = s === "aprovada"
              ? "background:#d1fae5;color:#065f46"
              : s === "rejeitada"
              ? "background:#fee2e2;color:#991b1b"
              : "background:#fef3c7;color:#92400e";
            return `<tr>
              <td>${fmtDHM(o.created_at)}</td>
              <td>${o.vigilante_nome || "—"}</td>
              <td>${o.descricao || o.tipo || "—"}</td>
              <td><span style="padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600;${sStyle}">${s}</span></td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>`;

  return `
    <div class="header">
      <div class="logo-area">
        <h1>Vigia Comando — Liberty Place</h1>
        <p>Grupo Esquematiza · Adm. Robson Freitas</p>
        <p style="margin-top:4px;">${periodoLabel}</p>
      </div>
      <div class="data-geracao">
        <p>Relatório gerado em</p>
        <strong>${geradoEm.toLocaleString("pt-BR")}</strong>
      </div>
    </div>

    <div class="cards">
      <div class="card"><div class="card-label">Pontos de Ronda</div><div class="card-value" style="color:#1d4ed8;">${dados.rondas.length}</div></div>
      <div class="card"><div class="card-label">Ocorrências</div><div class="card-value" style="color:#b45309;">${dados.ocorrencias.length}</div></div>
      <div class="card"><div class="card-label">Alertas de Emerg.</div><div class="card-value" style="color:#b91c1c;">${dados.alertas.length}</div></div>
      <div class="card"><div class="card-label">Passagens de Turno</div><div class="card-value" style="color:#15803d;">${dados.passagens.length}</div></div>
    </div>

    <div class="secao">
      <h2>Pontos confirmados por hora</h2>
      <div class="grafico">${graficoHTML}</div>
      <p style="font-size:11px;color:#9ca3af;text-align:center;">00h – 23h · total: ${dados.rondas.length} scan(s)</p>
    </div>

    <div class="secao">
      <h2>Rondas — pontos confirmados (${dados.rondas.length})</h2>
      ${rondasHTML}
    </div>

    <div class="secao">
      <h2>Ocorrências (${dados.ocorrencias.length})</h2>
      ${ocorrenciasHTML}
    </div>

    <div class="rodape">
      Relatório gerado em ${geradoEm.toLocaleString("pt-BR")} · Vigia Comando / Grupo Esquematiza
    </div>
  `;
}

function imprimirRelatorio(dados) {
  const janela = window.open("", "_blank");
  janela.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Relatório - Liberty Place</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; color: #1a1a1a; padding: 32px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #e5e7eb; }
    .logo-area h1 { font-size: 22px; font-weight: bold; color: #1e3a5f; }
    .logo-area p { color: #6b7280; font-size: 13px; margin-top: 4px; }
    .data-geracao { text-align: right; font-size: 12px; color: #6b7280; }
    .cards { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 16px; margin: 24px 0; }
    .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; }
    .card-label { font-size: 11px; font-weight: bold; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
    .card-value { font-size: 32px; font-weight: bold; margin-top: 8px; }
    .secao { margin: 24px 0; }
    .secao h2 { font-size: 13px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; color: #374151; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { background: #f9fafb; padding: 8px 12px; text-align: left; font-size: 11px; font-weight: bold; color: #6b7280; text-transform: uppercase; border-bottom: 1px solid #e5e7eb; }
    td { padding: 8px 12px; border-bottom: 1px solid #f3f4f6; color: #374151; }
    tr:last-child td { border-bottom: none; }
    .grafico { display: flex; align-items: flex-end; gap: 6px; height: 100px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 8px; }
    .barra-wrap { display: flex; flex-direction: column; align-items: center; flex: 1; height: 100%; justify-content: flex-end; }
    .barra { background: #3b82f6; border-radius: 3px 3px 0 0; width: 100%; min-height: 3px; }
    .barra-label { font-size: 9px; color: #9ca3af; margin-top: 4px; }
    .rodape { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; text-align: center; }
    .vazio { color: #9ca3af; font-style: italic; font-size: 13px; padding: 12px 0; }
  </style>
</head>
<body>
  ${gerarHTMLRelatorio(dados)}
  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`);
  janela.document.close();
}

const RelatorioModal = ({ relatorio, onClose }) => {
  const hoje  = new Date(); hoje.setHours(0, 0, 0, 0);
  const amanha = new Date(hoje); amanha.setDate(amanha.getDate() + 1);

  const [dados, setDados] = useState({ rondas: [], ocorrencias: [], alertas: [], passagens: [], loading: true });

  const carregar = useCallback(async () => {
    const [r1, r2, r3, r4] = await Promise.all([
      supabase.from("liberty_ronda_pontos").select("*").eq("empresa_id", EMPRESA_ID_REL)
        .gte("created_at", hoje.toISOString()).lt("created_at", amanha.toISOString()).order("created_at"),
      supabase.from("liberty_ocorrencias").select("*").eq("empresa_id", EMPRESA_ID_REL)
        .gte("created_at", hoje.toISOString()).lt("created_at", amanha.toISOString()).order("created_at"),
      supabase.from("alertas_panico").select("*")
        .gte("created_at", hoje.toISOString()).lt("created_at", amanha.toISOString()).order("created_at"),
      supabase.from("liberty_passagem_turno").select("*").eq("empresa_id", EMPRESA_ID_REL)
        .gte("created_at", hoje.toISOString()).lt("created_at", amanha.toISOString()).order("created_at"),
    ]);
    setDados({ rondas: r1.data||[], ocorrencias: r2.data||[], alertas: r3.data||[], passagens: r4.data||[], loading: false });
  }, []); // eslint-disable-line

  useEffect(() => { carregar(); }, [carregar]);

  const porHora = Array.from({ length: 24 }, (_, h) => ({
    hora:  h,
    total: dados.rondas.filter((r) => new Date(r.created_at).getHours() === h).length,
  }));
  const maxHora = Math.max(...porHora.map((h) => h.total), 1);

  const fmtHM  = (iso) => iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour:"2-digit", minute:"2-digit" }) : "—";
  const fmtDHM = (iso) => iso ? new Date(iso).toLocaleString("pt-BR",     { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }) : "—";
  const geradoEm     = new Date();
  const periodoLabel = hoje.toLocaleDateString("pt-BR", { weekday:"long", day:"2-digit", month:"long", year:"numeric" });

  return (
    <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-[900px] max-h-[92vh] flex flex-col rounded-2xl bg-[#0f1117] border border-white/10 shadow-2xl overflow-hidden">

        {/* ── Barra superior ── */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-white/10 bg-[#0a1128] shrink-0">
          <p className="text-sm font-semibold text-slate-200">{relatorio.nome}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={dados.loading}
              onClick={() => imprimirRelatorio(dados)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-300 hover:bg-white/10 transition disabled:opacity-40"
            >
              <Printer className="w-3.5 h-3.5" /> Imprimir / Baixar
            </button>
            <button type="button" onClick={onClose}
              className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center">
              <X className="w-4 h-4 text-slate-300" />
            </button>
          </div>
        </div>

        {/* ── Corpo com scroll ── */}
        <div className="overflow-y-auto flex-1">
          {dados.loading ? (
            <div className="flex items-center justify-center py-24 text-slate-500 text-sm">Carregando dados…</div>
          ) : (
            <div className="p-8 space-y-8">

              {/* 1 ── CABEÇALHO ── */}
              <header className="flex items-start justify-between gap-6 pb-6 border-b border-white/10">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#4f8ef7]/20 border border-[#4f8ef7]/40 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-[#4f8ef7]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#4f8ef7] font-semibold uppercase tracking-widest">Grupo Esquematiza</p>
                      <h1 className="text-xl font-black text-white leading-tight">Vigia Comando</h1>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Condomínio <span className="font-semibold text-white">Liberty Place</span>
                    {" · "}Adm. Robson Freitas
                  </p>
                  <p className="text-slate-500 text-xs mt-1">Período: {periodoLabel}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider">Relatório gerado em</p>
                  <p className="text-sm font-mono text-slate-300 mt-0.5">{geradoEm.toLocaleString("pt-BR")}</p>
                </div>
              </header>

              {/* 2 ── CARDS ── */}
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Resumo do dia</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label:"Pontos de Ronda",  valor: dados.rondas.length,      cor:"#4f8ef7", bg:"rgba(79,142,247,0.12)",  border:"rgba(79,142,247,0.3)"  },
                    { label:"Ocorrências",       valor: dados.ocorrencias.length, cor:"#f59e0b", bg:"rgba(245,158,11,0.12)",  border:"rgba(245,158,11,0.3)"  },
                    { label:"Alertas de Emerg.", valor: dados.alertas.length,     cor:"#ef4444", bg:"rgba(239,68,68,0.12)",   border:"rgba(239,68,68,0.3)"   },
                    { label:"Passagens Turno",   valor: dados.passagens.length,   cor:"#22c55e", bg:"rgba(34,197,94,0.12)",   border:"rgba(34,197,94,0.3)"   },
                  ].map(({ label, valor, cor, bg, border }) => (
                    <div key={label} style={{ background: bg, borderColor: border }} className="rounded-2xl border p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: cor }}>{label}</p>
                      <p className="text-3xl font-black text-white">{valor}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* 3 ── GRÁFICO ── */}
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Pontos confirmados por hora</h2>
                <div className="rounded-2xl bg-[#1a1d2e] border border-white/10 p-5">
                  <div style={{ display:"flex", alignItems:"flex-end", height:"160px", gap:"4px" }}>
                    {porHora.map(({ hora, total }) => (
                      <div key={hora} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:"4px" }}>
                        <div style={{
                          width:"100%",
                          height: total > 0 ? `${Math.round((total / maxHora) * 140)}px` : "3px",
                          background: total > 0 ? "#4f8ef7" : "rgba(255,255,255,0.05)",
                          borderRadius:"4px 4px 0 0",
                          minHeight: total > 0 ? "4px" : undefined,
                        }} />
                        <p style={{ fontSize:"9px", color:"#475569", fontFamily:"monospace", lineHeight:1 }}>
                          {String(hora).padStart(2,"0")}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-600 mt-3 text-center">Hora do dia (00h – 23h) · total: {dados.rondas.length} scan(s)</p>
                </div>
              </section>

              {/* 4 ── TABELA RONDAS ── */}
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
                  Rondas — pontos confirmados ({dados.rondas.length})
                </h2>
                {dados.rondas.length === 0 ? (
                  <p className="text-sm text-slate-500 italic py-4">Nenhum ponto registrado hoje.</p>
                ) : (
                  <div className="rounded-xl overflow-hidden border border-white/10">
                    <table className="w-full text-sm">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Horário</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Vigilante</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Ponto confirmado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {dados.rondas.map((r) => (
                          <tr key={r.id} className="hover:bg-white/[0.03]">
                            <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{fmtHM(r.created_at)}</td>
                            <td className="px-4 py-2.5 text-slate-200">{r.vigilante_nome || "—"}</td>
                            <td className="px-4 py-2.5 text-slate-300">{r.ponto || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* 5 ── TABELA OCORRÊNCIAS ── */}
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
                  Ocorrências ({dados.ocorrencias.length})
                </h2>
                {dados.ocorrencias.length === 0 ? (
                  <p className="text-sm text-slate-500 italic py-4">Nenhuma ocorrência registrada hoje.</p>
                ) : (
                  <div className="rounded-xl overflow-hidden border border-white/10">
                    <table className="w-full text-sm">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Horário</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Vigilante</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Descrição</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {dados.ocorrencias.map((o) => (
                          <tr key={o.id} className="hover:bg-white/[0.03]">
                            <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{fmtDHM(o.created_at)}</td>
                            <td className="px-4 py-2.5 text-slate-200">{o.vigilante_nome || "—"}</td>
                            <td className="px-4 py-2.5 text-slate-300 max-w-[240px] truncate">{o.descricao || o.tipo || "—"}</td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                o.status === "aprovada"  ? "bg-emerald-500/20 text-emerald-300" :
                                o.status === "rejeitada" ? "bg-red-500/20 text-red-300" :
                                                           "bg-amber-500/20 text-amber-300"
                              }`}>{o.status || "pendente"}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* 6 ── RODAPÉ ── */}
              <footer className="pt-6 border-t border-white/10 flex items-center justify-between gap-4">
                <p className="text-xs text-slate-500">
                  Relatório gerado em {geradoEm.toLocaleString("pt-BR")} · Vigia Comando / Grupo Esquematiza
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={dados.loading}
                    onClick={() => imprimirRelatorio(dados)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-300 hover:bg-white/10 transition disabled:opacity-40"
                  >
                    <Printer className="w-3.5 h-3.5" /> Imprimir / Baixar
                  </button>
                  <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-xl bg-[#4f8ef7] text-xs font-semibold text-white hover:bg-blue-400 transition">
                    Fechar
                  </button>
                </div>
              </footer>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const baixarArquivo = (nomeArquivo, conteudo) => {
  const blob = new Blob([conteudo], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const RelatorioCard = ({ titulo, descricao, tipo, onVer, onBasico, onDetalhado, somenteVer = false }) => {
  return (
    <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 hover:border-white/20 transition">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="tiny-label text-slate-500">Relatório</p>
          <h3 className="text-lg font-bold mt-1">{titulo}</h3>
        </div>

        <div className="w-10 h-10 rounded-xl bg-[#0d1b3e] border border-white/10 flex items-center justify-center">
          <FileText className="w-5 h-5 text-blue-300" />
        </div>
      </div>

      <p className="text-sm text-slate-400 leading-relaxed mb-5">
        {descricao}
      </p>

      <div className="rounded-xl bg-white/5 border border-white/10 p-3 mb-4">
        <p className="text-[11px] text-slate-500">Tipo</p>
        <p className="text-sm text-slate-200 capitalize">{tipo}</p>
      </div>

      {somenteVer ? (
        /* Botões "Básico" e "Detalhado" ocultos temporariamente — reativar removendo somenteVer */
        <button
          type="button"
          onClick={onVer}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-200 hover:bg-white/10 transition"
        >
          <Eye className="w-3.5 h-3.5" />
          Ver relatório
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={onVer}
            className="flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-200 hover:bg-white/10 transition"
          >
            <Eye className="w-3.5 h-3.5" />
            Ver
          </button>

          <button
            type="button"
            onClick={onBasico}
            className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-xs text-slate-200 hover:bg-white/10 transition"
          >
            Básico
          </button>

          <button
            type="button"
            onClick={onDetalhado}
            className="px-3 py-2.5 rounded-xl bg-emerald-500 text-xs text-black font-semibold hover:bg-emerald-400 transition"
          >
            Detalhado
          </button>
        </div>
      )}

    </div>
  );
};

const KpiCard = ({ titulo, valor, detalhe, icon: Icon }) => {
  return (
    <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="tiny-label text-slate-500">{titulo}</p>
          <p className="text-3xl font-bold mt-4">{valor}</p>
          <p className="text-xs text-slate-400 mt-1">{detalhe}</p>
        </div>

        <div className="w-10 h-10 rounded-xl bg-[#0d1b3e] border border-white/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-emerald-300" />
        </div>
      </div>
    </div>
  );
};

const FiltroFake = ({ icon: Icon, label }) => {
  return (
    <button
      type="button"
      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10 transition"
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RouteAudit />
        <Toaster
          theme="dark"
          position="top-center"
          toastOptions={{
            style: {
              background: "#0a1128",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#f8fafc",
            },
          }}
        />

        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />

          <Route
            path="/vigilante"
            element={
              <RoleRoute allow={["vigilante", ...desktopRoles]}>
                <PlantaoHome />
              </RoleRoute>
            }
          />

          <Route
            path="/checklist"
            element={
              <RoleRoute allow={["vigilante", ...desktopRoles]}>
                <ChecklistPage />
              </RoleRoute>
            }
          />

          <Route
            path="/historico"
            element={
              <RoleRoute allow={["vigilante", ...desktopRoles]}>
                <HistoricoPage />
              </RoleRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <RoleRoute allow={desktopRoles}>
                <Dashboard />
              </RoleRoute>
            }
          />

          <Route
            path="/sindico"
            element={
              <RoleRoute allow={desktopRoles}>
                <VigilantesDesktop />
              </RoleRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <RoleRoute allow={desktopRoles}>
                <Dashboard />
              </RoleRoute>
            }
          />

          <Route
            path="/registros"
            element={
              <RoleRoute allow={desktopRoles}>
                <RegistrosPage />
              </RoleRoute>
            }
          />

          <Route
            path="/relatorios"
            element={
              <RoleRoute allow={desktopRoles}>
                <RelatoriosPage />
              </RoleRoute>
            }
          />

          <Route
            path="/rondas"
            element={
              <ResponsiveRoute
                mobile={<RondasMobile />}
                desktop={<RondasDesktop />}
              />
            }
          />

          <Route
            path="/ocorrencias"
            element={
              <ResponsiveRoute
                mobile={<OcorrenciasMobile />}
                desktop={<OcorrenciasDesktop />}
              />
            }
          />

          <Route
            path="/passagem-turno"
            element={
              <ResponsiveRoute
                mobile={<PassagemTurnoMobile />}
                desktop={<PassagemTurnoDesktop />}
              />
            }
          />

          {/* Supervisor — fila de aprovação de ocorrências */}
          <Route
            path="/supervisor/ocorrencias"
            element={
              <RoleRoute allow={["supervisor", "admin", "liberty", "esquematiza"]}>
                <FilaAprovacao />
              </RoleRoute>
            }
          />

          {/* Supervisor — efetivo atual */}
          <Route
            path="/supervisor/efetivo"
            element={
              <RoleRoute allow={["supervisor", "admin", "liberty", "esquematiza"]}>
                <EfetivoAtual />
              </RoleRoute>
            }
          />

          {/* Supervisor — passagem de plantão */}
          <Route
            path="/supervisor/passagem"
            element={
              <RoleRoute allow={["supervisor", "admin", "liberty", "esquematiza"]}>
                <PassagemPlantao />
              </RoleRoute>
            }
          />

          {/* Treinamento — mobile para vigilante, desktop para supervisores */}
          <Route
            path="/treinamento"
            element={
              <ResponsiveRoute
                mobile={<TreinamentoPage />}
                desktop={<TreinamentosSupervision />}
              />
            }
          />

          {/* Supervisor — visão de treinamentos (acesso direto pelo painel) */}
          <Route
            path="/supervisor/treinamentos"
            element={
              <RoleRoute allow={desktopRoles}>
                <TreinamentosSupervision />
              </RoleRoute>
            }
          />

          {/* Avisos (mural) — vigilantes e supervisores */}
          <Route
            path="/avisos"
            element={
              <RoleRoute allow={["vigilante", ...desktopRoles]}>
                <AvisosPage />
              </RoleRoute>
            }
          />

          {/* Escala de 7 dias — síndico e supervisores */}
          <Route
            path="/escala"
            element={
              <RoleRoute allow={desktopRoles}>
                <EscalaDesktop />
              </RoleRoute>
            }
          />

          {/* Passagem de ponto — encerramento de turno */}
          <Route
            path="/passagem"
            element={
              <RoleRoute allow={["vigilante"]}>
                <PassagemPonto />
              </RoleRoute>
            }
          />

          {/* Perfil do vigilante */}
          <Route
            path="/perfil"
            element={
              <RoleRoute allow={["vigilante"]}>
                <PerfilPage />
              </RoleRoute>
            }
          />

          {/* QR scan — público, sem restrição de role */}
          <Route path="/ronda" element={<RondaQR />} />

          {/* Admin — QR codes para impressão */}
          <Route
            path="/admin/qrcodes"
            element={
              <RoleRoute allow={desktopRoles}>
                <QRCodesPage />
              </RoleRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;